<template>
  <div class="app-container">
    <!-- 标题和详情链接 -->
    <div class="analysis-header">
      <div class="analysis-title">考试分析</div>
      <router-link to="/score-analysis/score/list" class="detail-link">详情 <i class="el-icon-arrow-right"></i></router-link>
    </div>

    <!-- 统计卡片区域 -->
    <div class="stats-container">
      <div class="stat-card">
        <div class="stat-title">优秀率</div>
        <div class="stat-value">{{ excellentRate }}%</div>
        <el-progress type="circle" :percentage="excellentRate" :width="80" :color="excellentRateColors"></el-progress>
        <div class="stat-extra-info">
          <div class="extra-item">及格率 {{ passRate }}%</div>
          <div class="extra-item">进步率 {{ improvementRate }}%</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-title">平均分</div>
        <div class="stat-value">{{ averageScore }} <span class="unit">分</span></div>
        <el-progress type="circle" :percentage="scorePercentage" :width="80" :color="scoreColors"></el-progress>
        <div class="stat-extra-info">
          <div class="extra-item">中位数 {{ medianScore }}</div>
          <div class="extra-item">标准差 {{ standardDeviation }}</div>
        </div>
      </div>
    </div>

    <!-- 各班均分区域 -->
    <div class="score-distribution">
      <div class="distribution-title">各班均分</div>
      <div class="distribution-chart">
        <el-row :gutter="20">
          <el-col :span="16">
            <div ref="scoreChart" style="width: 100%; height: 300px;"></div>
          </el-col>
          <el-col :span="8">
            <div class="score-legend">
              <div class="legend-item" v-for="(item, index) in classScores" :key="index">
                <div class="color-block" :style="{ backgroundColor: classColors[index % classColors.length] }"></div>
                <div class="legend-text">{{ item.name }} {{ item.value }} 分</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 各班优秀率区域 -->
    <div class="excellent-rate-distribution">
      <div class="distribution-title">各班优秀率</div>
      <div class="distribution-chart">
        <el-row :gutter="20">
          <el-col :span="16">
            <div ref="excellentRateChart" style="width: 100%; height: 300px;"></div>
          </el-col>
          <el-col :span="8">
            <div class="excellent-rate-legend">
              <div class="legend-item" v-for="(item, index) in classExcellentRates" :key="index">
                <div class="color-block" :style="{ backgroundColor: classColors[index % classColors.length] }"></div>
                <div class="legend-text">{{ item.name }} {{ item.value }}%</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 数学知识点掌握情况雷达图 -->
    <div class="math-knowledge-distribution">
      <div class="distribution-title">数学知识点掌握情况</div>
      <div class="distribution-chart">
        <el-row :gutter="20">
          <el-col :span="16">
            <div ref="mathKnowledgeChart" style="width: 100%; height: 350px;"></div>
          </el-col>
          <el-col :span="8">
            <div class="math-knowledge-legend">
              <div class="legend-item" v-for="(item, index) in mathKnowledgePoints" :key="index">
                <div class="color-block" :style="{ backgroundColor: '#409EFF' }"></div>
                <div class="legend-text">{{ item.name }}: {{ item.value }}分</div>
              </div>
            </div>
            <div class="note">满分: 100分</div>
          </el-col>
        </el-row>
      </div>
    </div>

    <router-view></router-view>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import * as echarts from 'echarts'

export default {
  name: 'exam-analysis',
  data() {
    return {
      examCount: 15, // 考试数量
      passRate: 89, // 及格率
      excellentRate: 45, // 优秀率
      improvementRate: 32, // 进步率
      averageScore: 75, // 平均分
      medianScore: 78, // 中位数
      standardDeviation: 8.5, // 标准差
      // 各班均分数据
      classScores: [
        { name: '三年级一班', value: 82 },
        { name: '三年级二班', value: 78 },
        { name: '三年级三班', value: 85 },
        { name: '三年级四班', value: 76 },
        { name: '三年级五班', value: 81 }
      ],
      // 各班优秀率数据
      classExcellentRates: [
        { name: '三年级一班', value: 52 },
        { name: '三年级二班', value: 38 },
        { name: '三年级三班', value: 65 },
        { name: '三年级四班', value: 35 },
        { name: '三年级五班', value: 48 }
      ],
      // 数学知识点掌握情况数据
      mathKnowledgePoints: [
        { name: '数与代数', value: 85 },
        { name: '图形与几何', value: 72 },
        { name: '统计与概率', value: 68 },
        { name: '应用题', value: 76 },
        { name: '计算能力', value: 88 },
        { name: '空间想象', value: 65 }
      ],
      // 班级颜色
      classColors: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399'],
      normalHeartRateCount: 110, // 正常心率人数
      abnormalHeartRateCount: 9, // 异常心率人数
      examList: [], // 考试列表
      submissions: [], // 提交记录
      scoreChart: null, // 分数图表实例
      excellentRateChart: null, // 优秀率图表实例
      mathKnowledgeChart: null, // 数学知识点图表实例
      loading: false,
      roles: window.localStorage.getItem('roles') // 从localStorage获取roles
    }
  },
  computed: {
    ...mapGetters([
    ]),
    // 计算平均分对应的百分比（用于进度条）
    scorePercentage() {
      return Math.min(100, Math.round(this.averageScore))
    },
    // 分数进度条颜色
    scoreColors() {
      return [
        { color: '#F56C6C', percentage: 60 },
        { color: '#E6A23C', percentage: 75 },
        { color: '#67C23A', percentage: 90 },
        { color: '#409EFF', percentage: 100 }
      ]
    },
    // 优秀率进度条颜色
    excellentRateColors() {
      return [
        { color: '#F56C6C', percentage: 20 },
        { color: '#E6A23C', percentage: 40 },
        { color: '#67C23A', percentage: 60 },
        { color: '#409EFF', percentage: 100 }
      ]
    }
  },
  created() {
    // 检查用户角色
    if (!this.roles || !this.roles.includes('teacher')) {
      this.$message.error('您没有权限访问此页面')
      this.$router.push('/404')
    }
  },
  mounted() {
    this.fetchExamData()
    this.initScoreChart()
    this.initExcellentRateChart()
    this.initMathKnowledgeChart()
  },
  methods: {
    // 获取考试数据
    async fetchExamData() {
      this.loading = true
      try {
        // 获取考试列表
        const examRes = await axios.get('/api/score/list')
        if (examRes.data && examRes.data.code === 1) {
          this.examList = examRes.data.data
          // 更新考试数量
          this.examCount = this.examList.length

          // 获取提交记录
          const submissionsRes = await axios.get('/api/score/submissions')
          if (submissionsRes.data) {
            this.submissions = submissionsRes.data
            this.calculateStatistics()
          }
        }
      } catch (error) {
        console.error('获取考试数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 计算统计数据
    calculateStatistics() {
      if (!this.submissions || this.submissions.length === 0) {
        return
      }

      // 计算及格率
      const totalSubmissions = this.submissions.length
      const passedSubmissions = this.submissions.filter(s => s.score >= 60).length
      this.passRate = Math.round((passedSubmissions / totalSubmissions) * 100)

      // 计算优秀率（假设优秀为85分以上）
      const excellentSubmissions = this.submissions.filter(s => s.score >= 85).length
      this.excellentRate = Math.round((excellentSubmissions / totalSubmissions) * 100)

      // 计算平均分
      const scoredSubmissions = this.submissions.filter(s => s.score !== null && s.score !== undefined)
      if (scoredSubmissions.length > 0) {
        const totalScore = scoredSubmissions.reduce((sum, s) => sum + s.score, 0)
        this.averageScore = Math.round(totalScore / scoredSubmissions.length)
      }

      // 更新图表
      this.updateScoreChart()
      this.updateExcellentRateChart()
      this.updateMathKnowledgeChart()
    },

    // 初始化分数分布图表
    initScoreChart() {
      this.$nextTick(() => {
        if (this.$refs.scoreChart) {
          this.scoreChart = echarts.init(this.$refs.scoreChart)
          this.updateScoreChart()
        }
      })
    },

    // 更新分数分布图表
    updateScoreChart() {
      if (!this.scoreChart) return

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: '{b}: {c}分'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.classScores.map(item => item.name),
          axisLabel: {
            interval: 0,
            rotate: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '均分',
          min: 60,
          max: 100
        },
        series: [
          {
            name: '班级均分',
            type: 'bar',
            barWidth: '30%',
            data: this.classScores.map((item, index) => {
              return {
                value: item.value,
                itemStyle: {
                  color: this.classColors[index % this.classColors.length]
                }
              }
            }),
            label: {
              show: true,
              position: 'top',
              formatter: '{c}分'
            }
          }
        ]
      }

      this.scoreChart.setOption(option)
    },

    // 初始化优秀率图表
    initExcellentRateChart() {
      this.$nextTick(() => {
        if (this.$refs.excellentRateChart) {
          this.excellentRateChart = echarts.init(this.$refs.excellentRateChart)
          this.updateExcellentRateChart()
        }
      })
    },

    // 更新优秀率图表
    updateExcellentRateChart() {
      if (!this.excellentRateChart) return

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: '{b}: {c}%'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.classExcellentRates.map(item => item.name),
          axisLabel: {
            interval: 0,
            rotate: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '优秀率',
          min: 0,
          max: 100,
          axisLabel: {
            formatter: '{value}%'
          }
        },
        series: [
          {
            name: '班级优秀率',
            type: 'bar',
            barWidth: '30%',
            data: this.classExcellentRates.map((item, index) => {
              return {
                value: item.value,
                itemStyle: {
                  color: this.classColors[index % this.classColors.length]
                }
              }
            }),
            label: {
              show: true,
              position: 'top',
              formatter: '{c}%'
            }
          }
        ]
      }

      this.excellentRateChart.setOption(option)
    },

    // 初始化数学知识点雷达图
    initMathKnowledgeChart() {
      this.$nextTick(() => {
        if (this.$refs.mathKnowledgeChart) {
          this.mathKnowledgeChart = echarts.init(this.$refs.mathKnowledgeChart)
          this.updateMathKnowledgeChart()
        }
      })
    },

    // 更新数学知识点雷达图
    updateMathKnowledgeChart() {
      if (!this.mathKnowledgeChart) return

      const option = {
        tooltip: {
          trigger: 'item'
        },
        radar: {
          indicator: this.mathKnowledgePoints.map(item => {
            return { name: item.name, max: 100 }
          }),
          radius: '65%',
          center: ['50%', '50%'],
          name: {
            textStyle: {
              color: '#606266',
              fontSize: 14
            }
          },
          splitArea: {
            areaStyle: {
              color: ['rgba(64, 158, 255, 0.1)', 'rgba(64, 158, 255, 0.2)']
            }
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(64, 158, 255, 0.5)'
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(64, 158, 255, 0.5)'
            }
          }
        },
        series: [{
          name: '知识点掌握情况',
          type: 'radar',
          data: [{
            value: this.mathKnowledgePoints.map(item => item.value),
            name: '掌握程度',
            areaStyle: {
              color: 'rgba(64, 158, 255, 0.4)'
            },
            lineStyle: {
              color: '#409EFF',
              width: 2
            },
            itemStyle: {
              color: '#409EFF'
            }
          }]
        }]
      }

      this.mathKnowledgeChart.setOption(option)
    }
  },
  beforeDestroy() {
    // 销毁图表实例
    if (this.scoreChart) {
      this.scoreChart.dispose()
      this.scoreChart = null
    }
    if (this.excellentRateChart) {
      this.excellentRateChart.dispose()
      this.excellentRateChart = null
    }
    if (this.mathKnowledgeChart) {
      this.mathKnowledgeChart.dispose()
      this.mathKnowledgeChart = null
    }
  }
}
</script>

<style scoped>
/* 标题和详情链接样式 */
.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.analysis-title {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
}

.detail-link {
  font-size: 14px;
  color: #909399;
  text-decoration: none;
  display: flex;
  align-items: center;
}

.detail-link:hover {
  color: #409EFF;
}

.detail-link i {
  margin-left: 5px;
}

/* 统计卡片样式 */
.stats-container {
  display: flex;
  margin-bottom: 20px;
}

.stat-card {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-right: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-title {
  font-size: 16px;
  color: #606266;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 36px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 15px;
}

.unit {
  font-size: 16px;
  font-weight: normal;
}

.stat-extra-info {
  margin-top: 10px;
  width: 100%;
  display: flex;
  justify-content: space-around;
}

.extra-item {
  font-size: 14px;
  color: #606266;
}

/* 分数分布样式 */
.score-distribution, .excellent-rate-distribution, .heart-rate-distribution, .math-knowledge-distribution {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.distribution-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
}

.score-legend, .excellent-rate-legend, .heart-rate-legend, .math-knowledge-legend {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.color-block {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  margin-right: 10px;
}

.legend-text {
  font-size: 14px;
  color: #606266;
}

.note {
  font-size: 14px;
  color: #909399;
  margin-top: 10px;
}
</style>
